import { computed, makeObservable, observable, runInAction } from 'mobx';
import {
  Account,
  AccountStateKey,
  AccountStatus,
  AccountType,
  AssetClass,
  ConsistencyRules,
  TradingSystem,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import AccountService from '@hub/services/entities/account-service';
import { EntityStore } from '../base/entity-store';

type DefaultOverviewFilters = {
  type: AccountType[];
  status: AccountStatus[];
  state: AccountStateKey[];
  assetClass: AssetClass | null;
};

const DEFAULT_OVERVIEW_FILTERS: DefaultOverviewFilters = {
  type: [],
  status: [],
  state: [],
  assetClass: null,
};

export class AccountStore extends EntityStore<AccountService, Account> {
  @observable
  overviewFilters: DefaultOverviewFilters = DEFAULT_OVERVIEW_FILTERS;

  constructor() {
    const {
      apiFactory: { accountService },
    } = getEnv();

    super(accountService);
    makeObservable(this);
  }

  @computed
  get isDefaultOverviewFilters() {
    return (
      JSON.stringify(this.overviewFilters) ===
      JSON.stringify(DEFAULT_OVERVIEW_FILTERS)
    );
  }

  async decryptPassword(login: string, tradingSystem: TradingSystem) {
    try {
      const {
        apiFactory: { accountService },
      } = getEnv();

      return await accountService.decryptPassword(login, tradingSystem);
    } catch (e) {
      console.error(`Failed decrypting password`, e);
    }
  }

  getByPurchaseUniqId(purchaseUniqId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getByPurchaseUniqId(purchaseUniqId);
  }

  getBalanceChart(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getBalanceChart(tsAccountId);
  }

  getBalance(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getBalance(tsAccountId);
  }

  getMidnightHistory(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getMidnightHistory(tsAccountId);
  }

  sync(tsAccountId: string, force?: boolean) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.sync(tsAccountId, force);
  }

  getConsistencyRules(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getConsistencyRules(tsAccountId);
  }

  getConsistencyRule(tsAccountId: string, rule: ConsistencyRules) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getConsistencyRule(tsAccountId, rule);
  }

  getStats(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getStats(tsAccountId);
  }

  getDailyPerformance(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getDailyPerformance(tsAccountId);
  }

  getByTradingSystemAccount(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.getByTradingSystemAccount(tsAccountId);
  }

  selectPartner(orderUniqId: string, partnerId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.selectPartner(orderUniqId, partnerId);
  }

  makeBannerAsSeen(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.markBannerAsSeen(tsAccountId);
  }

  markTerminationReasonAsSeen(tsAccountId: string) {
    const {
      apiFactory: { accountService },
    } = getEnv();

    return accountService.markTerminationReasonAsSeen(tsAccountId);
  }

  /** Optimistically set terminationReasonSeen on the cached account so the modal stays closed without refetch. */
  setTerminationReasonSeenLocally(tsAccountId: string): void {
    runInAction(() => {
      const account = this.items.find(
        (a) => (a as Account).tsAccountId === tsAccountId,
      );
      if (account) {
        const acc = account as Account;
        acc.config = {
          ...acc.config,
          hasSeen: acc.config?.hasSeen ?? false,
          terminationReasonSeen: true,
        };
      }
    });
  }

  @computed
  get getOverviewFilters() {
    return this.overviewFilters;
  }

  setOverviewFilters(filters: DefaultOverviewFilters) {
    runInAction(() => {
      this.overviewFilters = filters;
    });
  }
}
